import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Consultant/AddConsultant.css";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import * as XLSX from "xlsx";
import { createConsultant } from "../../../services/consultantService";
import { getConsultantTypes } from "../../../services/metadataService";
import NormalAlert from "../../AlertComponent/NormalAlert";

const AddConsultant = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    consultantCode: "",
    consultantName: "",
    email: "",
    phoneNumber: "",
    consultantType: "",
  });

  const [errors, setErrors] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [alert, setAlert] = useState(null);

  const [consultantTypes, setConsultantTypes] = useState([]);

  //  Bulk Import States
  const [uploadedJson, setUploadedJson] = useState([]);
  const [uploadedExcelFile, setUploadedExcelFile] = useState(null);

  useEffect(() => {
    const fetchTypes = async () => {
      const types = await getConsultantTypes();
      setConsultantTypes(types);
    };
    fetchTypes();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.consultantCode.trim())
      newErrors.consultantCode = "Consultant Code is required.";
    if (!formData.consultantName.trim())
      newErrors.consultantName = "Consultant Name is required.";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required.";
    else if (!/^\d{10}$/.test(formData.phoneNumber))
      newErrors.phoneNumber = "Phone must be exactly 10 digits.";
    if (!formData.email.trim()) newErrors.email = "Email is required.";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Invalid email format.";
    if (!formData.consultantType.trim())
      newErrors.consultantType = "Consultant Type is required.";

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const response = await createConsultant(formData);
      const data = response.data;

      if (data.status === "Success") {
        setAlert({ type: data.status, message: data.message });
        setTimeout(() => {
          navigate("/dashboard");
        }, 1500);
      } else {
        setAlert({
          type: "fail",
          message: data.message || "Something went wrong.",
        });
      }
    } catch (error) {
      console.log(error);
      setAlert({
        type: "fail",
        message: error?.response?.data?.message || "Server error occurred.",
      });
    }
  };

  const initialFormData = {
    consultantCode: "",
    consultantName: "",
    email: "",
    phoneNumber: "",
    consultantType: "",
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setErrors({});
  };

  //  Bulk Import Handlers
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadedExcelFile(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws);
      setUploadedJson(data);
      console.log("Parsed JSON:", data);
    };
    reader.readAsBinaryString(file);
  };

  const handleDownloadSample = () => {
    const wb = XLSX.utils.book_new();
    const sampleData = [
      [
        "First Name",
        "Last Name",
        "Phone",
        "Email",
        "Company",
        "Interest",
        "Comments",
      ],
    ];
    const ws = XLSX.utils.aoa_to_sheet(sampleData);
    XLSX.utils.book_append_sheet(wb, ws, "Sample");
    XLSX.writeFile(wb, "Consultant_Sample.xlsx");
  };

  const handleDownloadJson = () => {
    const blob = new Blob([JSON.stringify(uploadedJson, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "consultants.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadUploadedExcel = () => {
    if (!uploadedExcelFile) return;
    const url = URL.createObjectURL(uploadedExcelFile);
    const link = document.createElement("a");
    link.href = url;
    link.download = uploadedExcelFile.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="SetuSbg"></div>
      <div className="form-over-image w-100">
        <div className="add-consultant-wrapper mb-4">
          <div
            className="container p-5 my-5 shadow bg-white rounded role-form-container add-consultant-card shadow-lg"
            style={{ maxWidth: "900px" }}
          >
            <div className="form-header">
              <div className="gap-2">
                <i className="bi bi-person-fill avatar-icon"></i>
                <h2 className="form-title">Add Consultant</h2>
              </div>

              <button
                className="btn btn-primary px-5 py-2"
                onClick={() => setShowModal(true)}
              >
                Bulk Import
              </button>
            </div>

            <hr className="text-dark" />
            <form onSubmit={handleSubmit}>
              <div className="row g-3">
                <div className="form-group col-md-6 col-12 mb-3">
                  <label className="form-label text-black">
                    Consultant Code<span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    type="text"
                    name="consultantCode"
                    placeholder="Consultant Code"
                    value={formData.employeeCode}
                    onChange={handleChange}
                    className={`input100 form-control ${
                      errors.consultantCode ? "is-invalid" : ""
                    }`}
                  />
                  {errors.consultantCode && (
                    <div className="invalid-feedback">
                      {errors.consultantCode}
                    </div>
                  )}
                </div>

                <div className="form-group col-md-6 col-12 mb-3">
                  <label className="form-label text-black">
                    Consultant Name<span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    type="text"
                    name="consultantName"
                    placeholder="Enter full name"
                    value={formData.consultantName}
                    onChange={handleChange}
                    className={`input100 form-control ${
                      errors.consultantName ? "is-invalid" : ""
                    }`}
                  />
                  {errors.consultantName && (
                    <div className="invalid-feedback">
                      {errors.consultantName}
                    </div>
                  )}
                </div>

                <div className="form-group col-md-6 col-12 mb-3">
                  <label className="form-label text-black">
                    Email<span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`input100 form-control ${
                      errors.email ? "is-invalid" : ""
                    }`}
                  />
                  {errors.email && (
                    <div className="invalid-feedback">{errors.email}</div>
                  )}
                </div>

                <div className="form-group col-md-6 col-12 mb-3">
                  <label className="form-label text-black">
                    Phone Number<span style={{ color: "red" }}> *</span>
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    placeholder="Enter phone"
                    value={formData.phoneNumber}
                    maxLength="10"
                    pattern="\d{10}"
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) {
                        handleChange(e);
                      }
                    }}
                    className={`input100 form-control ${
                      errors.phoneNumber ? "is-invalid" : ""
                    }`}
                  />
                  {errors.phoneNumber && (
                    <div className="invalid-feedback">{errors.phoneNumber}</div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label text-black">
                    Consultant Type<span style={{ color: "red" }}> *</span>
                  </label>
                  <select
                    name="consultantType"
                    className={`input100 form-control ${
                      errors.phoneNumber ? "is-invalid" : ""
                    }`}
                    value={formData.consultantType}
                    onChange={handleChange}
                  >
                    <option value="">-- Select Consultant Type --</option>
                    {consultantTypes.map((type) => (
                      <option
                        key={type.consultantTypeID}
                        value={type.consultantTypeID}
                      >
                        {type.consultantTypeName}
                      </option>
                    ))}
                  </select>
                  {errors.consultantType && (
                    <div className="invalid-feedback">
                      {errors.consultantType}
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex justify-content-center">
                <button
                  type="submit"
                  className="btn btn-primary px-5 py-2 mt-4 me-3"
                >
                  Submit
                </button>
                <button
                  type="reset"
                  className="btn btn-light px-5 py-2 mt-4 me-3"
                  onClick={handleReset}
                >
                  Reset
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* </div> */}

      {/*  Bulk Import Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Bulk Import Consultants</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileUpload}
              className="form-control"
            />
          </div>

          {uploadedJson.length > 0 && (
            <div className="mt-3">
              <div className="alert alert-success">
                ✅ Successfully parsed {uploadedJson.length} records.
              </div>
              <div className="d-flex gap-3">
                <button
                  className="btn btn-success"
                  onClick={handleDownloadJson}
                >
                  Download JSON
                </button>
                <button
                  className="btn btn-warning"
                  onClick={handleDownloadUploadedExcel}
                >
                  Download Uploaded Excel
                </button>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button
            className="consultant-btn px-5 py-2"
            onClick={() => setShowModal(false)}
          >
            Upload
          </Button>
        </Modal.Footer>
      </Modal>

      {alert && (
        <NormalAlert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}
    </>
  );
};

export default AddConsultant;
